import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Copy, Check, X, Envelope, ShieldStar } from "@phosphor-icons/react";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";

/* ============================================================================
   CoeInvitesTab — manual copy-link invitation flow.
   ----------------------------------------------------------------------------
   No email — admin clicks "Invite", fills in details, gets a magic accept URL,
   copies + sends via their channel of choice. The invitee opens the URL,
   confirms phone, and joins as a staff member with the pre-assigned role.

   This is intentionally low-tech for the v1; can be upgraded to Resend later
   without changing the data model.
   ========================================================================== */

const EMPTY = { name: "", email: "", staff_type: "counsellor", custom_role_id: "", note: "" };

export function CoeInvitesTab() {
    const [invites, setInvites] = useState([]);
    const [roles, setRoles] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [justCreated, setJustCreated] = useState(null); // {accept_url, token, name, email}

    const load = () => {
        api.get("/admin/coe/invites").then((r) => setInvites(r.data || [])).catch(() => {});
        api.get("/admin/coe/roles").then((r) => setRoles(r.data || [])).catch(() => {});
    };
    useEffect(load, []);

    const createInvite = async () => {
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Name and email are required");
            return;
        }
        setSaving(true);
        try {
            const r = await api.post("/admin/coe/invites", {
                ...form,
                custom_role_id: form.custom_role_id || null,
            });
            const inv = r.data;
            const acceptUrl = `${window.location.origin}/coe/accept-invite/${inv.token}`;
            setJustCreated({ ...inv, accept_url: acceptUrl });
            setOpen(false);
            setForm(EMPTY);
            load();
            toast.success("Invite created — copy the link below");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
        setSaving(false);
    };

    const revoke = async (id) => {
        if (!await confirmDialog({ title: "Revoke this invite?", confirmText: "Confirm", tone: "danger" })) return;
        try {
            await api.delete(`/admin/coe/invites/${id}`);
            toast.success("Revoked");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="space-y-5" data-testid="coe-invites-tab">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-display text-xl font-bold tracking-tight">Pending invites</h2>
                    <p className="text-xs text-muted-foreground">Manual copy-link flow — paste the link in WhatsApp, email, or SMS yourself.</p>
                </div>
                <Button onClick={() => setOpen(true)} className="rounded-full" data-testid="coe-invite-new">
                    <Plus size={14} className="mr-1.5" /> New invite
                </Button>
            </div>

            {justCreated && (
                <AcceptLinkBanner invite={justCreated} onDismiss={() => setJustCreated(null)} />
            )}

            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                {invites.length === 0 ? (
                    <div className="text-center text-sm text-muted-foreground py-10" data-testid="coe-invites-empty">
                        No pending invites. New members can be invited above.
                    </div>
                ) : (
                    <>
                        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                            <div className="col-span-3">Person</div>
                            <div className="col-span-3">Email</div>
                            <div className="col-span-3">Role</div>
                            <div className="col-span-2">Created</div>
                            <div className="col-span-1 text-right">Actions</div>
                        </div>
                        {invites.map((inv) => (
                            <InviteRow key={inv.id} invite={inv} onRevoke={() => revoke(inv.id)} />
                        ))}
                    </>
                )}
            </div>

            <NewInviteDialog
                open={open}
                onClose={() => setOpen(false)}
                form={form}
                setForm={setForm}
                roles={roles}
                saving={saving}
                onSubmit={createInvite}
            />
        </div>
    );
}

function InviteRow({ invite, onRevoke }) {
    const [copied, setCopied] = useState(false);
    const acceptUrl = `${window.location.origin}/coe/accept-invite/${invite.token}`;
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(acceptUrl);
            setCopied(true);
            toast.success("Accept link copied");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Could not copy");
        }
    };
    return (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-3 border-b border-border last:border-0 items-center" data-testid={`coe-invite-row-${invite.id}`}>
            <div className="md:col-span-3 font-semibold text-sm truncate">{invite.name}</div>
            <div className="md:col-span-3 text-xs text-muted-foreground truncate"><Envelope size={11} className="inline mr-1" />{invite.email}</div>
            <div className="md:col-span-3 text-xs flex items-center gap-1.5 min-w-0">
                <ShieldStar size={12} className="text-primary shrink-0" />
                <span className="font-medium truncate">{invite.staff_type}</span>
                {invite.custom_role_id && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary/10 text-primary uppercase font-bold tracking-wider">Custom</span>
                )}
            </div>
            <div className="md:col-span-2 text-[11px] text-muted-foreground">
                {new Date(invite.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </div>
            <div className="md:col-span-1 flex md:justify-end gap-1.5">
                <Button size="sm" variant="outline" onClick={copy} className="h-8" data-testid={`coe-invite-copy-${invite.id}`}>
                    {copied ? <Check size={12} /> : <Copy size={12} />}
                </Button>
                <Button size="sm" variant="outline" onClick={onRevoke} className="h-8 text-rose-600 border-rose-200" data-testid={`coe-invite-revoke-${invite.id}`}>
                    <X size={12} />
                </Button>
            </div>
        </div>
    );
}

function AcceptLinkBanner({ invite, onDismiss }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(invite.accept_url);
            setCopied(true);
            toast.success("Link copied — send it to the invitee");
            setTimeout(() => setCopied(false), 1800);
        } catch { toast.error("Copy failed"); }
    };
    return (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="coe-invite-link-banner">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-emerald-700">Invite created</div>
                    <div className="font-display text-lg font-bold mt-0.5">{invite.name}'s accept link</div>
                    <p className="text-xs text-emerald-800 mt-1">
                        Copy the link below and send it to <span className="font-semibold">{invite.email}</span> via your preferred channel. The link is single-use.
                    </p>
                </div>
                <button onClick={onDismiss} className="text-emerald-700 hover:text-emerald-900" aria-label="Dismiss">
                    <X size={14} />
                </button>
            </div>
            <div className="mt-3 flex gap-2 items-center">
                <Input readOnly value={invite.accept_url} className="font-mono text-xs flex-1 bg-white" data-testid="coe-invite-link-input" />
                <Button onClick={copy} className="rounded-full shrink-0" data-testid="coe-invite-link-copy">
                    {copied ? <><Check size={12} className="mr-1.5" /> Copied</> : <><Copy size={12} className="mr-1.5" /> Copy</>}
                </Button>
            </div>
        </div>
    );
}

function NewInviteDialog({ open, onClose, form, setForm, roles, saving, onSubmit }) {
    const builtIn = roles.filter((r) => r.is_built_in);
    const custom = roles.filter((r) => !r.is_built_in);
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="coe-new-invite-dialog">
                <DialogHeader>
                    <DialogTitle>Invite a new COE member</DialogTitle>
                    <DialogDescription>
                        You'll get an accept link to copy and send via WhatsApp / SMS / email. The invitee verifies their phone and joins automatically.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <Fld label="Full name" required>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="coe-invite-form-name" />
                    </Fld>
                    <Fld label="Email" required>
                        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="coe-invite-form-email" />
                    </Fld>
                    <Fld label="Built-in role">
                        <select value={form.staff_type} onChange={(e) => setForm({ ...form, staff_type: e.target.value })}
                            className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                            data-testid="coe-invite-form-staff-type">
                            {builtIn.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                        </select>
                    </Fld>
                    {custom.length > 0 && (
                        <Fld label="Custom role override">
                            <select value={form.custom_role_id} onChange={(e) => setForm({ ...form, custom_role_id: e.target.value })}
                                className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                                data-testid="coe-invite-form-custom-role">
                                <option value="">— None —</option>
                                {custom.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </select>
                        </Fld>
                    )}
                    <Fld label="Internal note (optional)">
                        <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="coe-invite-form-note" />
                    </Fld>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={onSubmit} disabled={saving || !form.name.trim() || !form.email.trim()} data-testid="coe-invite-form-submit">
                        {saving ? "Creating…" : "Create invite"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Fld({ label, required, children }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                {label} {required && <span className="text-rose-600">*</span>}
            </Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
